/**
 * Size and Spacing Design Tokens
 *
 * Size and spacing-specific token handling that uses the general token utilities.
 * This matches the SCSS core/sizes/_tokens.scss implementation.
 */

import { createTokenExtractor } from '../core/tokens';
import { type SpacingName, type SizeName, type RadiusName } from '../values/sizes';

/**
 * Spacing token extractor with type safety
 *
 * Matches SCSS: spacing($name)
 *
 * @param name - The name of the spacing token (must be a valid SpacingName)
 * @param fallback - Optional fallback value
 * @returns CSS var() function for the spacing token
 */
export const spacing = createTokenExtractor<SpacingName>('spacing');

/**
 * Size token extractor with type safety
 *
 * Matches SCSS: size($name)
 *
 * @param name - The name of the size token (must be a valid SizeName)
 * @param fallback - Optional fallback value
 * @returns CSS var() function for the size token
 */
export const size = createTokenExtractor<SizeName>('size');

/**
 * Border radius token extractor with type safety
 *
 * Matches SCSS: radius($name)
 *
 * @param name - The name of the radius token (must be a valid RadiusName)
 * @param fallback - Optional fallback value
 * @returns CSS var() function for the radius token
 */
export const radius = createTokenExtractor<RadiusName>('radius');

/**
 * Example usage:
 *
 * ```typescript
 * import { spacing, size, radius } from './sizes';
 *
 * const padding = spacing('md'); // ✅ Valid spacing name
 * const width = size('lg'); // ✅ Valid size name
 * const borderRadius = radius('sm'); // ✅ Valid radius name
 *
 * // TypeScript will show error for invalid names:
 * // const invalidSpacing = spacing('invalid-size'); // ❌ Type error
 * // const invalidSize = size('invalid-size'); // ❌ Type error
 * // const invalidRadius = radius('invalid-radius'); // ❌ Type error
 * ```
 */
